import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { BarChart3, Plus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';

/**
 * PollCard — render a single poll inline inside the group chat.
 * Props: { pollId, currentUserId, canDelete, onDeleted? }
 */
export const PollCard = ({ pollId, currentUserId, canDelete, onDeleted }) => {
  const [poll, setPoll] = useState(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`/api/polls/${pollId}`).then(r => { if (alive) setPoll(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [pollId]);

  if (!poll) return null;
  const totalVotes = (poll.options || []).reduce((s, o) => s + ((o.votes || []).length), 0);
  const closed = poll.closed || (poll.closes_at && new Date(poll.closes_at) < new Date());

  const vote = async (optId) => {
    if (closed || voting) return;
    setVoting(true);
    try {
      const r = await axios.post(`/api/polls/${pollId}/vote`, { option_id: optId });
      setPoll(r.data.poll);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vote impossible');
    } finally { setVoting(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer ce sondage ?')) return;
    try { await axios.delete(`/api/polls/${pollId}`); onDeleted?.(); }
    catch { toast.error('Suppression impossible'); }
  };

  return (
    <div className="bg-violet-500/10 border border-violet-500/30 rounded-2xl p-3 max-w-[85%]" data-testid={`poll-${pollId}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-violet-300" />
          <p className="text-xs font-semibold text-violet-200">{closed ? 'Sondage clos' : 'Sondage'}</p>
        </div>
        {canDelete && (
          <button onClick={remove} className="p-1 hover:bg-red-500/20 rounded-full" title="Supprimer">
            <Trash2 className="w-3.5 h-3.5 text-red-300" />
          </button>
        )}
      </div>
      <p className="text-sm font-semibold mb-2 text-white">{poll.question}</p>
      <div className="space-y-1.5">
        {(poll.options || []).map(opt => {
          const votes = (opt.votes || []).length;
          const pct = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
          const youVoted = (opt.votes || []).includes(currentUserId);
          return (
            <button
              key={opt.id}
              disabled={closed || voting}
              onClick={() => vote(opt.id)}
              className={`relative w-full text-left rounded-xl px-3 py-2 overflow-hidden transition-colors ${
                youVoted ? 'bg-violet-600/40 border border-violet-400' : 'bg-white/5 hover:bg-white/10 border border-white/10'
              } ${closed ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid={`poll-option-${pollId}-${opt.id}`}
            >
              <div className="absolute inset-0 bg-violet-500/15" style={{ width: `${pct}%` }} aria-hidden />
              <div className="relative flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{opt.text}</span>
                <span className="text-xs text-white/70 shrink-0">{votes} · {pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400 mt-2">{totalVotes} vote{totalVotes > 1 ? 's' : ''}{poll.multi_select ? ' · choix multiples' : ''}</p>
    </div>
  );
};

/**
 * CreatePollModal — admin form to create a new poll.
 * Props: { open, onClose, groupId, onCreated }
 */
export const CreatePollModal = ({ open, onClose, groupId, onCreated }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multi, setMulti] = useState(false);
  const [duration, setDuration] = useState(24);
  const [busy, setBusy] = useState(false);
  const kbHeight = useKeyboardHeight();

  if (!open) return null;
  const valid = question.trim().length >= 2 && options.filter(o => o.trim()).length >= 2;
  const setOpt = (i, v) => setOptions(prev => prev.map((p, idx) => idx === i ? v : p));
  const addOpt = () => setOptions(prev => prev.length < 10 ? [...prev, ''] : prev);
  const rmOpt = (i) => setOptions(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = {
        question: question.trim(),
        options: options.map(o => o.trim()).filter(Boolean),
        multi_select: multi,
        duration_hours: Number(duration) || 24,
      };
      const r = await axios.post(`/api/groups/${groupId}/polls`, body);
      toast.success('Sondage créé');
      onCreated?.(r.data.poll);
      setQuestion(''); setOptions(['', '']); setMulti(false); setDuration(24);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Création impossible');
    } finally { setBusy(false); }
  };

  return ReactDOM.createPortal((
    <div
      className="fixed inset-0 z-[2147483647] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="create-poll-modal"
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}
    >
      <div
        className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '90vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-300" />
            Nouveau sondage
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full"><X className="w-5 h-5 text-gray-300" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="text-xs text-gray-400">Question</label>
            <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Quel film regarde-t-on ce soir ?" maxLength={300}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
              data-testid="poll-question-input" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Options ({options.length}/10)</label>
            <div className="mt-1 space-y-1.5">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={opt} onChange={e => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} maxLength={120}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
                    data-testid={`poll-option-input-${i}`} />
                  {options.length > 2 && (
                    <button onClick={() => rmOpt(i)} className="p-2 hover:bg-red-500/20 rounded-full"><X className="w-4 h-4 text-red-300" /></button>
                  )}
                </div>
              ))}
              {options.length < 10 && (
                <button onClick={addOpt} className="text-xs text-violet-300 hover:text-white flex items-center gap-1" data-testid="add-poll-option">
                  <Plus className="w-3 h-3" /> Ajouter une option
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-400">Choix multiples</label>
            <button onClick={() => setMulti(!multi)} className={`w-12 h-6 rounded-full transition-colors ${multi ? 'bg-violet-600' : 'bg-white/10'}`} data-testid="poll-multi-toggle">
              <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${multi ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div>
            <label className="text-xs text-gray-400">Durée (heures, max 168)</label>
            <input type="number" min="1" max="168" value={duration} onChange={e => setDuration(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
              data-testid="poll-duration-input" />
          </div>
          <button onClick={submit} disabled={!valid || busy}
            className="w-full mt-2 py-2.5 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 font-semibold text-white"
            data-testid="poll-submit-btn">
            {busy ? 'Création…' : 'Créer le sondage'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};
